import { CHAT_TRANSLATE_URL } from "../constants/api";

/**
 * Host–guest chat translation, ported from the host mobile app: the original
 * message is never changed, the translation is only used for display.
 * Unlike the mobile app there are no third-party fallbacks — when the endpoint
 * doesn't translate, the original text is shown.
 */

/** App language codes → the language codes /ai/translate expects. */
const TRANSLATE_LANG_CODES = {
  EN: "en",
  AR: "ar",
  FR: "fr",
  RU: "ru",
  HI: "hi",
  DE: "de",
  KO: "ko",
  ES: "es",
  JA: "ja",
  PH: "fil",
  TH: "th",
  PT: "pt",
  NL: "nl",
  CH: "zh-CN",
  "CH-HANS": "zh-CN",
  "ZH-HANS": "zh-CN",
  "CH-HANT": "zh-TW",
};

/** Returns null for a language we don't know, so nothing gets translated. */
export const toTranslateLangCode = (appLang) =>
  TRANSLATE_LANG_CODES[String(appLang || "").toUpperCase()] || null;

export const isChatTranslationEnabled = () => Boolean(CHAT_TRANSLATE_URL);

const RTL_CHARS =
  /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
const LEADING_PUNCTUATION = /^([!？?؟.¡¿…]+)(\s*)/;
const TRAILING_PUNCTUATION = /[!？?؟.¡¿…]+$/;

/**
 * The model tends to put a sentence's "!" or "؟" at the front of Arabic or
 * Persian output; move it back to the end. Only for translated text.
 */
export const fixRtlLeadingPunctuation = (input) => {
  const text = String(input || "").trim();
  if (!text || !RTL_CHARS.test(text)) return input;
  const match = LEADING_PUNCTUATION.exec(text);
  if (!match) return input;
  const rest = text.slice(match[0].length).trimEnd();
  if (!rest) return input;
  return TRAILING_PUNCTUATION.test(rest) ? rest : rest + match[1];
};

// Letters and digits only: the model sometimes returns same-language text
// with only diacritics, accents or punctuation changed (مرحبا → مرحباً)
const comparable = (value) =>
  String(value)
    .normalize("NFKD")
    .replace(/[^\p{L}\p{N}]/gu, "")
    .toLowerCase();

const sameText = (a, b) => comparable(a) === comparable(b);

// `${targetLang}\0${text}` → translated text, or null when it came back unchanged
const cache = new Map();
const inflight = new Map();

export const clearTranslationCache = () => {
  cache.clear();
  inflight.clear();
};

/**
 * Translate a chat message into targetLang (a code from toTranslateLangCode),
 * authorised by the chat guest id. Resolves to the translated text, or null
 * when there is nothing to show instead of the original (request failed, or
 * the text is already in targetLang). Never rejects. Failures are not cached,
 * so a later call retries.
 */
export const translateChatText = (text, targetLang, guestId) => {
  const source = String(text || "");
  // Nothing to translate in emoji-, number- or punctuation-only messages
  if (
    !targetLang ||
    !guestId ||
    !isChatTranslationEnabled() ||
    !/\p{L}/u.test(source)
  ) {
    return Promise.resolve(null);
  }

  const key = `${targetLang}\u0000${source}`;
  if (cache.has(key)) return Promise.resolve(cache.get(key));
  const pending = inflight.get(key);
  if (pending) return pending;

  const request = fetch(CHAT_TRANSLATE_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json; charset=utf-8",
      Authorization: `Bearer ${guestId}`,
    },
    body: JSON.stringify({ text: source, sourceLang: "auto", targetLang }),
  })
    .then((response) => (response.ok ? response.json() : null))
    .then((data) => {
      // Already in targetLang: nothing to translate, and no need to ask again
      if (data?.reason === "empty_or_identical") {
        cache.set(key, null);
        return null;
      }
      const translated =
        data?.status === "translated"
          ? String(data.translatedText ?? "").trim()
          : "";
      if (!translated) return null;
      const result = sameText(translated, source)
        ? null
        : fixRtlLeadingPunctuation(translated);
      cache.set(key, result);
      return result;
    })
    .catch(() => null)
    .finally(() => inflight.delete(key));

  inflight.set(key, request);
  return request;
};
